// Safe, lightweight Markdown: input is HTML-escaped first, then a small set of constructs is turned into tags.
// Supported: # / ## / ### headings, - • * lists, 1. lists, **bold**, *italic*, [text](url), bare links, e-mail and tel: links.
// underline from the editor (<u>…</u>) is carried through as two private-use markers, not as ++…++ (a typed
// "+" would collide); decodeEntities never produces private-use characters, so typed text cannot fake them
const U_OPEN = String.fromCharCode(0xe001);
const U_CLOSE = String.fromCharCode(0xe002);
const UNDERLINE = new RegExp(`${U_OPEN}([^${U_OPEN}${U_CLOSE}]*)${U_CLOSE}`, 'g');
const MARKERS = new RegExp(`[${U_OPEN}${U_CLOSE}]`, 'g');
const esc = (s: string) =>
  s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

// Links are found in the RAW text and built from escaped parts; regexes never run over generated HTML.
// URLs stop at whitespace, brackets, quotes and angle brackets, so they cannot break out of the href attribute.
// (the underline markers end a URL too: an underlined address stays a working link)
const URL_CHARS = String.raw`https?:\/\/[^\s()<>"'\x60\[\]${U_OPEN}${U_CLOSE}]+`;
// inside [text](…) a web address may contain one level of brackets (Wikipedia: …/Seite_(Begriff))
const URL_IN_LINK = String.raw`https?:\/\/(?:[^\s()<>"'\x60\[\]${U_OPEN}${U_CLOSE}]|\([^\s()<>"'\x60\[\]]{0,200}\)){1,2000}`;
// e-mail addresses (the editor links a typed address as [a@b.de](mailto:a@b.de); an address typed without a space
// after it stays text, with "_" saved as "\_"). Parts are bounded (no slow matching on long texts); no "%" (a
// %-encoded "," would add a hidden recipient).
const EMAIL = String.raw`(?:[A-Za-z0-9.+-]|\\?_){1,64}@[A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63}){0,8}\.[A-Za-z]{2,24}`;
// phone links from pasted text (spaces allowed, the link gets the digits)
const TEL = String.raw`\+?[0-9][0-9 ./-]{0,30}`;
// anything else in (…) – ftp:, javascript:, relative links …: only the text is shown, never a link.
// No spaces (a link target has none): "[ca. 20 €](vor Ort zu zahlen)" in an old text stays text as written.
const REST = String.raw`(?:[^\s()]|\([^\s()]{0,200}\)){0,2000}`;
// one label, then the link target decides (the label is matched once – fast also with many "[")
// (no lookbehind: older iPhones cannot parse it – a bare address starts after a non-address character instead)
const LINK = new RegExp(
  [
    String.raw`\[([^\]\n]{1,500})\]\((?:(${URL_IN_LINK})|mailto:(${EMAIL})(?:\?[^\s()<>"'\x60\[\]]{0,500})?|tel:(${TEL})|${REST})\)`,
    // 1 text; 2 https://…, 3 mailto: (without ?subject=…), 4 tel:, none of them: another link type (text only)
    `(${URL_CHARS})`, // 5: bare https://…
    String.raw`(^|[^A-Za-z0-9._+\\-])(${EMAIL})`, // 6, 7: bare e-mail address (6: the character before it)
  ].join('|'),
  'gi',
);
/** a letter or digit right before an address (müller@…): part of a longer word – not linked */
const IN_WORD = /[\p{L}\p{N}]/u;
const anchor = (href: string, label: string) =>
  `<a href="${esc(href)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
/** mailto: / tel: open the mail or phone app – no new tab */
const appLink = (href: string, label: string) => `<a href="${esc(href)}">${label}</a>`;
const telHref = (t: string) => `tel:${t.replace(/[^+0-9]/g, '')}`;
const emphasis = (escaped: string) =>
  escaped
    // backslash escapes from the editor (\* \_ \# …) stay plain characters: as entities they match no rule below
    .replace(/\\([!-/:-@[-`{-~])/g, (_m, c: string) => `&#${c.charCodeAt(0)};`)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/~~([^~]+)~~/g, '<s>$1</s>') // strikethrough
    .replace(/\+\+([^+]+)\+\+/g, '<u>$1</u>') // underline written as ++…++
    .replace(UNDERLINE, '<u>$1</u>') // underline from the editor (see legacyHtml)
    .replace(MARKERS, ''); // an unpaired marker (e.g. split by a link) is dropped

// links are put in as numbered slots, so **bold** / *italic* around a link works over the whole line
const S_OPEN = String.fromCharCode(0xe003);
const S_CLOSE = String.fromCharCode(0xe004);
const SLOT = new RegExp(`${S_OPEN}(\\d+)${S_CLOSE}`, 'g');
const SLOT_CHARS = new RegExp(`[${S_OPEN}${S_CLOSE}]`, 'g');

function inline(input: string) {
  const raw = input.replace(SLOT_CHARS, ''); // typed text cannot fake a slot
  const links: string[] = [];
  const put = (html: string) => `${S_OPEN}${links.push(html) - 1}${S_CLOSE}`;
  let text = '',
    last = 0;
  const find = new RegExp(LINK.source, LINK.flags); // own position: after a typed "[" the search goes on right after it
  for (let m = find.exec(raw); m; m = find.exec(raw)) {
    text += raw.slice(last, m.index);
    last = m.index + m[0].length;
    if (m[1] !== undefined) {
      if (/(^|[^\\])(\\\\)*\\$/.test(raw.slice(Math.max(0, m.index - 50), m.index))) {
        // (an odd number of backslashes escapes the "[": "\\[a](…)" is a typed backslash and then a link)
        // a typed "[" ("\[…\](…)"), not a link: only the bracket is text – an address inside is still linked
        text += '[';
        last = find.lastIndex = m.index + 1;
      } else if (m[2] !== undefined) text += put(anchor(m[2], emphasis(esc(m[1]))));
      else if (m[3] !== undefined) text += put(appLink(`mailto:${m[3].replace(/\\/g, '')}`, emphasis(esc(m[1]))));
      else if (m[4] !== undefined) text += put(appLink(telHref(m[4]), emphasis(esc(m[1]))));
      else text += m[1]; // another link type: only the text
    } else if (m[7] !== undefined) {
      const address = m[7].replace(/\\/g, '');
      text += IN_WORD.test(m[6]) ? m[6] + m[7] : m[6] + put(appLink(`mailto:${address}`, esc(address)));
    } else {
      // bare link: trailing punctuation belongs to the sentence, not to the URL
      const url = m[5].replace(/[.,;:!?*_~]+$/, ''); // also the end of **bold** / *italic* / ~~strike~~
      // only "https://" left (e.g. "https://***"): plain text
      text += /^https?:\/\/.+/i.test(url) ? put(anchor(url, esc(url))) : url;
      last -= m[5].length - url.length;
    }
  }
  return emphasis(esc(text + raw.slice(last))).replace(SLOT, (_m, i: string) => links[Number(i)]);
}

/**
 * Bare e-mail addresses become explicit links before the text goes into the editor: its Markdown reader would
 * otherwise link only part of "first\_last@web.de" (the editor saves a typed "_" as "\_").
 */
export const linkBareEmails = (t: string) =>
  t
    // HTML tags from the old Sportplaner (<a href="mailto:…">) stay as they are
    .split(/(<[a-z/][^<>]{0,2000}>)/i)
    .map((part, i) =>
      i % 2
        ? part
        : part.replace(LINK, (m0: string, ...g: unknown[]) => {
            const before = g[5] as string | undefined;
            const address = g[6] as string | undefined;
            return address !== undefined && !IN_WORD.test(before ?? '')
              ? `${before ?? ''}[${address}](mailto:${address.replace(/\\/g, '')})`
              : m0;
          }),
    )
    .join('');

/**
 * Simple HTML from the old Sportplaner (<b>, <strong>, <i>, <em>, <s>, <strike>, <del>, <br>, <p>) → Markdown.
 * <u> stays (the editor writes underline as <u>…</u>). Any other HTML is left alone and later shown as plain text.
 */
export const legacyTags = (t: string) =>
  String(t || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n\n')
    .replace(/<\/?p>/gi, '\n')
    .replace(/<(b|strong)>([\s\S]*?)<\/\1>/gi, '**$2**')
    .replace(/<(i|em)>([\s\S]*?)<\/\1>/gi, '*$2*')
    .replace(/<(s|strike|del)>([\s\S]*?)<\/\1>/gi, '~~$2~~')
    .replace(/<U>([\s\S]*?)<\/U>/g, '<u>$1</u>');
/**
 * For showing a text: old tags converted, real <u> tags as ++…++ (BEFORE decoding – a typed "<u>", stored as
 * "&lt;u&gt;", stays text), then entities decoded.
 */
export const legacyHtml = (t: string) =>
  decodeEntities(legacyTags(t).replace(/<u>([\s\S]*?)<\/u>/gi, `${U_OPEN}$1${U_CLOSE}`));
/**
 * For loading a text into the editor: old tags converted and named entities of the old Sportplaner (&auml; &nbsp; …)
 * decoded – but NOT &amp; &lt; &gt; &quot;: the editor decodes those itself, and a decoded "<x>" would be read as an
 * HTML tag there.
 */
export const editorText = (t: string) =>
  linkBareEmails(
    legacyTags(t).replace(/&([A-Za-z]{2,8});/g, (m, e: string) =>
      // &amp; &lt; &gt; &quot; for the editor in the lower-case form it decodes (&AMP; would stay literal there)
      ['amp', 'lt', 'gt', 'quot'].includes(e.toLowerCase())
        ? `&${e.toLowerCase()};`
        : (ENTITIES[e] ?? ENTITIES[e.toLowerCase()] ?? m),
    ),
  );

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  auml: 'ä',
  ouml: 'ö',
  uuml: 'ü',
  Auml: 'Ä',
  Ouml: 'Ö',
  Uuml: 'Ü',
  szlig: 'ß',
  euro: '€',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  bdquo: '„',
  ldquo: '“',
  rdquo: '”',
  sbquo: '‚',
  lsquo: '‘',
  rsquo: '’',
  laquo: '«',
  raquo: '»',
  eacute: 'é',
  egrave: 'è',
  agrave: 'à',
  deg: '°',
  middot: '·',
  bull: '•',
  times: '×',
  copy: '©',
};
/**
 * The editor stores & < > as HTML entities (&amp; &lt; &gt;, so typed text is never read as HTML); the old
 * Sportplaner had entities too. Turned back into characters in ONE pass – a typed "&lt;" (stored "&amp;lt;") stays
 * "&lt;". Safe: everything is escaped again afterwards (md) or shown as plain text (stripMd). Control characters
 * and invalid code points stay as they were.
 */
export const decodeEntities = (t: string) =>
  t.replace(/&(#x[0-9a-f]{1,6}|#\d{1,7}|[a-z]{2,8});/gi, (m, e: string) => {
    if (e.startsWith('#')) {
      const n = e[1].toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : Number(e.slice(1));
      // no control characters, no surrogates, no private-use characters (used internally as markers)
      const ok =
        (n >= 32 || n === 9 || n === 10) && n <= 0x10ffff && (n < 0xd800 || n > 0xdfff) && (n < 0xe000 || n > 0xf8ff);
      return ok ? String.fromCodePoint(n) : m;
    }
    return ENTITIES[e] ?? ENTITIES[e.toLowerCase()] ?? m;
  });

export function md(text: string): string {
  const lines = legacyHtml(text).replace(/\r/g, '').split('\n');
  let out = '',
    para: string[] = [];
  let list: { tag: 'ul' | 'ol'; items: string[] } | null = null;
  const flushP = () => {
    if (para.length) {
      out += `<p>${para.map(inline).join('<br>')}</p>`;
      para = [];
    }
  };
  const flushL = () => {
    if (list) {
      out += `<${list.tag}>${list.items.map(i => `<li>${inline(i)}</li>`).join('')}</${list.tag}>`;
      list = null;
    }
  };
  for (const raw of lines) {
    const l = raw.trimEnd();
    let m: RegExpMatchArray | null;
    if (!l.trim()) {
      flushP();
      flushL();
      continue;
    }
    if ((m = l.match(/^\s*(#{1,3})\s+(.+)/))) {
      flushP();
      flushL();
      const lv = m[1].length + 2;
      out += `<h${lv}>${inline(m[2])}</h${lv}>`;
      continue;
    }
    if ((m = l.match(/^\s*[-*•–]\s+(.+)/))) {
      flushP();
      if (!list || list.tag !== 'ul') {
        flushL();
        list = { tag: 'ul', items: [] };
      }
      list.items.push(m[1]);
      continue;
    }
    if ((m = l.match(/^\s*\d+[.)]\s+(.+)/))) {
      flushP();
      if (!list || list.tag !== 'ol') {
        flushL();
        list = { tag: 'ol', items: [] };
      }
      list.items.push(m[1]);
      continue;
    }
    flushL();
    para.push(l.trim());
  }
  flushP();
  flushL();
  return out;
}

// backslash escapes from the editor (\* \_ \[ …) are typed characters, not formatting: set aside as private-use
// characters U+E021–U+E07E (the escapable ASCII characters + 0xE000) while the formatting is removed, then put back
const hideEscapes = (t: string) =>
  t.replace(/\\([!-/:-@[-`{-~])/g, (_m, c: string) => String.fromCharCode(0xe000 + c.charCodeAt(0)));
const ESCAPED = new RegExp(`[${String.fromCharCode(0xe021)}-${String.fromCharCode(0xe07e)}]`, 'g');
const showEscapes = (t: string) => t.replace(ESCAPED, c => String.fromCharCode(c.charCodeAt(0) - 0xe000));
export const stripMd = (t: string) =>
  showEscapes(
    hideEscapes(legacyHtml(t))
      .replace(MARKERS, '')
      .replace(/^\s*#{1,3}\s+/gm, '')
      .replace(/^\s*(?:[-*•–]|\d+[.)])\s+/gm, '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/~~([^~]+)~~/g, '$1')
      .replace(/\+\+([^+]+)\+\+/g, '$1')
      // (as in the renderer: bounded, one level of brackets, no spaces – except in a phone number)
      .replace(
        /\[([^\]\n]{1,500})\]\((tel:\+?[0-9][0-9 ./-]{0,30}|(?:[^\s()]|\([^\s()]{0,200}\)){0,2000})\)/gi,
        (_m, label: string, href: string) => plainLink(label, href.trim()),
      ),
  );
/**
 * A link as plain text (tooltips, previews, the text part of mails): "Anmeldung (https://…)"; only the address when
 * the text is the address; e-mail and phone without "mailto:"/"tel:"; other link types (ftp:, javascript: …) text only.
 */
function plainLink(label: string, href: string) {
  const m = /^(?:(https?:\/\/\S+)|mailto:([^?]+)(?:\?.*)?|tel:(.+))$/i.exec(href);
  if (!m) return label;
  const address = m[1] ?? m[2]?.replace(/\\/g, '') ?? m[3].replace(/[^+0-9]/g, '');
  // compared without editor escapes (\_), web prefix, trailing slash and case – phone numbers by their digits
  const bare = (s: string) =>
    m[3] !== undefined
      ? s.replace(/[^0-9]/g, '').replace(/^49/, '0')
      : showEscapes(s)
          .replace(/\\/g, '')
          .replace(/^https?:\/\//i, '')
          .replace(/\/$/, '')
          .toLowerCase();
  return bare(label) === bare(address) ? label : `${label} (${address})`;
}
export const firstLine = (t: string) =>
  stripMd(t)
    .split(/\n+/)
    .map(s => s.trim())
    .filter(Boolean)
    .join(' · ');

/** Hover text for an event (title, time, place, description as plain text) – shown by the browser as a tooltip. */
export function eventTooltip(e: { title: string; description: string; location: string }, when: string, max = 600) {
  const desc = stripMd(e.description)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  const head = [e.title, [when, e.location].filter(Boolean).join(' · ')].filter(Boolean).join('\n');
  return desc ? `${head}\n\n${desc.length > max ? `${desc.slice(0, max).trimEnd()} …` : desc}` : head;
}
